import React, { useEffect, useState, useCallback } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { Link } from 'react-router-dom';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import services from '../../services';

function formatMetric(cell) {
    if (!cell || cell.value === null || cell.value === undefined) {
        return 'Coming soon';
    }
    if (typeof cell.value === 'number' && Number.isFinite(cell.value)) {
        return cell.value.toLocaleString();
    }
    return String(cell.value);
}

function Gri() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await services.Gri.metrics();
            setData(res);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return <ThemedSpinner />;
    }

    if (error) {
        return (
            <Grid container direction="row" justify="center" spacing={2}>
                <Grid item xs={12}>
                    <Typography color="error">
                        Failed to load GRI metrics.
                    </Typography>
                    <Button color="primary" onClick={load}>
                        Retry
                    </Button>
                </Grid>
            </Grid>
        );
    }

    if (!data) {
        return <ThemedSpinner />;
    }

    const rows = [
        {
            label: 'Customer organisations count',
            cell: data.customerOrganisationsCount,
        },
        {
            label: 'Customer retention rate',
            cell: data.customerRetentionRate,
            suffix:
                data.customerRetentionRate &&
                data.customerRetentionRate.value != null
                    ? '%'
                    : '',
        },
        {
            label: 'Total users trained globally',
            cell: data.totalUsersTrainedGlobally,
            footnote:
                'Distinct learner per company (training completion events).',
        },
        {
            label: 'Number of countries our customers are in',
            cell: data.customerCountriesCount,
            footnote:
                'Distinct billing countries from Chargebee customers matched to included companies.',
        },
    ];

    return (
        <Grid container direction="row" justify="center" spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h4" gutterBottom>
                    GRI
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Sustainability reporting metrics. Exclusions are configured in{' '}
                    <Link to="/system/settings">System Settings</Link> using keys{' '}
                    <code>gri.exclude_company_id</code> and{' '}
                    <code>gri.exclude_name_contains</code> (add multiple rows with the
                    same key for multiple values). Figures are cached for about{' '}
                    {data.cacheTtlSeconds
                        ? Math.round(data.cacheTtlSeconds / 3600)
                        : 12}{' '}
                    hours per metric slice (Redis).
                </Typography>
            </Grid>
            <Grid item xs={12} md={10} lg={8}>
                <Paper variant="outlined">
                    <Box p={2}>
                        {rows.map((row) => (
                            <Box
                                key={row.label}
                                py={2}
                                style={{
                                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                                }}
                            >
                                <Typography variant="subtitle1" gutterBottom>
                                    {row.label}
                                </Typography>
                                <Typography variant="h5">
                                    {formatMetric(row.cell)}
                                    {row.suffix || ''}
                                </Typography>
                                {row.footnote && (
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        display="block"
                                    >
                                        {row.footnote}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Paper>
                {data.exclusions && (
                    <Box mt={2}>
                        <Typography variant="caption" color="textSecondary">
                            Excluded company IDs:{' '}
                            {data.exclusions.companyIds.join(', ') || '—'}; name
                            substrings:{' '}
                            {data.exclusions.nameContains.join(', ') || '—'}.
                        </Typography>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
}

export default Gri;
